import React from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../i18n/LocalizedLink';
import { usePageTitle } from '../hooks/usePageTitle';
import { SEOMeta } from '../templates/SEOMeta';
import { BaseLayout } from '../templates/BaseLayout';

const TermsOfServiceEs: React.FC = () => {
  return (
    <BaseLayout>
      <SEOMeta
        title="Términos y Condiciones - El Impostor Game"
        description="Términos y condiciones de uso de El Impostor Game. Lee las reglas y condiciones para usar nuestra plataforma de juego."
        canonical="https://el-impostor.com/terms-of-service"
        excludeAdsense={true}
      />
      <div className="min-h-screen bg-blue-50 dark:bg-gray-950 py-8 px-4">
        <div className="max-w-5xl mx-auto bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800">
          {/* Encabezado */}
          <div className="px-8 py-8 border-b border-gray-200 dark:border-gray-800">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3 tracking-tight">
              Términos y Condiciones de Uso
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Última actualización:{' '}
              {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Contenido */}
          <div className="px-8 py-8 space-y-8 text-gray-800 dark:text-gray-200 leading-relaxed text-left">
            {/* Bienvenida */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                1. Bienvenida y Aceptación
              </h2>
              <p className="text-justify">
                Bienvenido a <strong>El Impostor Game</strong>, una plataforma de juegos online operada por
                <strong> RurayLabs</strong>. Al acceder y utilizar nuestro sitio web y servicios, aceptas
                estar legalmente vinculado por estos Términos y Condiciones. Si no estás de acuerdo con
                alguna parte de estos términos, no debes utilizar nuestros servicios.
              </p>
            </section>

            {/* Descripción del Servicio */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                2. Descripción del Servicio
              </h2>
              <p className="text-justify">
                El Impostor Game es una plataforma de entretenimiento que ofrece juegos de deducción social 
                en línea. Nuestros servicios incluyen:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Salas de juego multijugador en tiempo real</li>
                <li>Modo de juego presencial (Mesa)</li>
                <li>Sistema de creación y gestión de salas de juego</li>
                <li>Comunicación en tiempo real entre jugadores</li>
                <li>Contenido educativo y guías en nuestro blog</li>
              </ul>
            </section>

            {/* Edad Mínima */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                3. Edad Mínima y Responsabilidad Parental
              </h2>
              <p className="text-justify">
                Nuestros servicios están diseñados para ser disfrutados por personas de todas las edades. 
                Sin embargo, establecemos las siguientes condiciones:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Los menores de 13 años deben usar el servicio bajo supervisión de un adulto responsable</li>
                <li>Los menores entre 13 y 18 años deben tener el consentimiento de sus padres o tutores</li>
                <li>Los padres y tutores son responsables de supervisar el uso que hacen los menores de nuestro servicio</li>
              </ul>
            </section>

            {/* Cuenta de Usuario */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                4. Uso del Servicio
              </h2>
              <p className="text-justify">
                Al utilizar El Impostor Game, te comprometes a:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Proporcionar información veraz cuando sea requerida</li>
                <li>No utilizar nombres de usuario ofensivos, vulgares o inapropiados</li>
                <li>Mantener un comportamiento respetuoso con otros usuarios</li>
                <li>No intentar interrumpir, hackear o comprometer la seguridad del servicio</li>
                <li>No utilizar bots, scripts o automatizaciones no autorizadas</li>
                <li>No utilizar el servicio para actividades ilegales o fraudulentas</li>
              </ul>
            </section>

            {/* Conducta del Usuario */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                5. Normas de Conducta
              </h2>
              <p className="text-justify">
                Para mantener un ambiente de juego positivo y seguro, prohibimos estrictamente:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Lenguaje ofensivo, amenazante, acosador o discriminatorio</li>
                <li>Contenido sexualmente explícito o pornográfico</li>
                <li>Incitación al odio por raza, religión, género, orientación sexual o cualquier otra característica</li>
                <li>Spam, publicidad no solicitada o promoción de servicios externos</li>
                <li>Suplantación de identidad o impersonación de otros usuarios</li>
                <li>Compartir información personal de otros usuarios sin su consentimiento</li>
                <li>Hacer trampa o intentar manipular el resultado de los juegos</li>
              </ul>
              <p className="text-justify mt-4">
                <strong>Nos reservamos el derecho de suspender o banear permanentemente</strong> a cualquier 
                usuario que viole estas normas.
              </p>
            </section>

            {/* Contenido del Usuario */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                6. Contenido del Usuario
              </h2>
              <p className="text-justify">
                El contenido que generas durante el juego (mensajes, nombres, etc.) es de tu responsabilidad. 
                Al publicar contenido en nuestra plataforma:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Declaras que tienes derecho a publicar ese contenido</li>
                <li>Nos otorgas una licencia no exclusiva para mostrar y almacenar ese contenido</li>
                <li>Aceptas que podemos moderar, editar o eliminar contenido inapropiado</li>
                <li>El contenido no almacena información personal sensible</li>
              </ul>
            </section>

            {/* Propiedad Intelectual */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                7. Propiedad Intelectual
              </h2>
              <p className="text-justify">
                Todo el contenido de El Impostor Game, incluyendo pero no limitado a:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Diseño, gráficos, logos e interfaz visual</li>
                <li>Código fuente y software</li>
                <li>Textos, artículos del blog y contenido escrito</li>
                <li>Mecánicas de juego y sistema de reglas</li>
              </ul>
              <p className="text-justify mt-4">
                Está protegido por derechos de autor y leyes de propiedad intelectual. 
                <strong> No está permitido copiar, reproducir, distribuir o crear trabajos derivados</strong> sin 
                nuestro consentimiento expreso por escrito.
              </p>
            </section>

            {/* Disponibilidad del Servicio */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                8. Disponibilidad y Mantenimiento
              </h2>
              <p className="text-justify">
                Nos esforzamos por mantener el servicio disponible 24/7, pero:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Podemos realizar mantenimientos programados con o sin previo aviso</li>
                <li>El servicio puede experimentar interrupciones por problemas técnicos</li>
                <li>No garantizamos disponibilidad ininterrumpida del servicio</li>
                <li>Podemos modificar, suspender o descontinuar características sin previo aviso</li>
              </ul>
            </section>

            {/* Limitación de Responsabilidad */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                9. Limitación de Responsabilidad
              </h2>
              <p className="text-justify">
                El Impostor Game se proporciona "tal cual" y "según disponibilidad". En la máxima medida 
                permitida por la ley:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>No somos responsables de pérdida de datos o interrupciones del servicio</li>
                <li>No garantizamos que el servicio esté libre de errores o virus</li>
                <li>No somos responsables del comportamiento de otros usuarios</li>
                <li>No somos responsables de daños indirectos, incidentales o consecuentes</li>
                <li>El servicio es gratuito y se usa bajo tu propio riesgo</li>
              </ul>
            </section>

            {/* Publicidad */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                10. Publicidad y Servicios de Terceros
              </h2>
              <p className="text-justify">
                Nuestro servicio puede mostrar publicidad de terceros a través de Google AdSense. Al usar nuestro servicio:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Aceptas que podemos mostrar anuncios en la plataforma</li>
                <li>Los anuncios pueden utilizar cookies para personalización</li>
                <li>No controlamos el contenido de los anuncios de terceros</li>
                <li>No somos responsables de las políticas de privacidad de los anunciantes</li>
              </ul>
            </section>

            {/* Modificaciones */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                11. Modificaciones de los Términos
              </h2>
              <p className="text-justify">
                Nos reservamos el derecho de modificar estos Términos y Condiciones en cualquier momento. 
                Cuando realicemos cambios significativos:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Actualizaremos la fecha de "Última actualización"</li>
                <li>Los cambios entrarán en vigor inmediatamente al publicarse</li>
                <li>El uso continuado del servicio implica aceptación de los nuevos términos</li>
                <li>Es tu responsabilidad revisar estos términos periódicamente</li>
              </ul>
            </section>

            {/* Terminación */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                12. Terminación del Servicio
              </h2>
              <p className="text-justify">
                Nos reservamos el derecho de:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Suspender o terminar tu acceso por violación de estos términos</li>
                <li>Cerrar salas de juego que infrinjan nuestras normas</li>
                <li>Eliminar contenido inapropiado sin previo aviso</li>
                <li>Modificar o descontinuar el servicio en cualquier momento</li>
              </ul>
            </section>

            {/* Ley Aplicable */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                13. Ley Aplicable y Jurisdicción
              </h2>
              <p className="text-justify">
                Estos Términos y Condiciones se rigen por las leyes de España. Cualquier disputa 
                relacionada con estos términos será sometida a la jurisdicción exclusiva de los tribunales 
                españoles.
              </p>
            </section>

            {/* Contacto */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                14. Contacto
              </h2>
              <p className="text-justify">
                Si tienes preguntas sobre estos Términos y Condiciones, puedes contactarnos:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  A través de nuestra{' '}
                  <LocalizedLink to="/contact" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium">
                    página de contacto
                  </LocalizedLink>
                </li>
                <li>Empresa: RurayLabs</li>
                <li>Sitio web: <a href="https://el-impostor.com" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline">el-impostor.com</a></li>
              </ul>
            </section>

            {/* Separabilidad */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                15. Separabilidad
              </h2>
              <p className="text-justify">
                Si alguna disposición de estos Términos se considera inválida o inaplicable, 
                las disposiciones restantes continuarán en pleno vigor y efecto.
              </p>
            </section>

            {/* Acuerdo Completo */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                16. Acuerdo Completo
              </h2>
              <p className="text-justify">
                Estos Términos y Condiciones, junto con nuestra{' '}
                <LocalizedLink to="/privacy-policy" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium">
                  Política de Privacidad
                </LocalizedLink>
                , constituyen el acuerdo completo entre tú y RurayLabs con respecto al uso de El Impostor Game.
              </p>
            </section>
          </div>

          {/* Footer del documento */}
          <div className="px-8 py-6 bg-gray-50 dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800">
            <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
              Al continuar usando El Impostor Game, aceptas estos Términos y Condiciones.
            </p>
            <div className="flex justify-center gap-4 mt-4">
              <LocalizedLink 
                to="/privacy-policy" 
                className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm font-medium"
              >
                Ver Política de Privacidad
              </LocalizedLink>
              <LocalizedLink 
                to="/contact" 
                className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm font-medium"
              >
                Contactar
              </LocalizedLink>
              <LocalizedLink 
                to="/" 
                className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm font-medium"
              >
                Volver al Inicio
              </LocalizedLink>
            </div>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

const TermsOfServicePt: React.FC = () => {
  return (
    <BaseLayout>
      <SEOMeta
        title="Termos e Condições - El Impostor Game"
        description="Termos e condições de uso do El Impostor Game. Leia as regras e condições para usar nossa plataforma de jogo."
        canonical="https://el-impostor.com/terms-of-service"
        excludeAdsense={true}
      />
      <div className="min-h-screen bg-blue-50 dark:bg-gray-950 py-8 px-4">
        <div className="max-w-5xl mx-auto bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800">
          {/* Encabezado */}
          <div className="px-8 py-8 border-b border-gray-200 dark:border-gray-800">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3 tracking-tight">
              Termos e Condições de Uso
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Última atualização:{' '}
              {new Date().toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Conteúdo */}
          <div className="px-8 py-8 space-y-8 text-gray-800 dark:text-gray-200 leading-relaxed text-left">
            {/* Boas-vindas */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                1. Boas-vindas e Aceitação
              </h2>
              <p className="text-justify">
                Bem-vindo ao <strong>El Impostor Game</strong>, uma plataforma de jogos online operada pela
                <strong> RurayLabs</strong>. Ao acessar e utilizar nosso site e serviços, você concorda em estar
                legalmente vinculado a estes Termos e Condições. Se você não concorda com alguma parte,
                não deve utilizar nossos serviços.
              </p>
            </section>

            {/* Descrição do Serviço */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                2. Descrição do Serviço
              </h2>
              <p className="text-justify">
                El Impostor Game é uma plataforma de entretenimento que oferece jogos de dedução social online.
                Nossos serviços incluem:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Salas de jogo multijogador em tempo real</li>
                <li>Modo de jogo presencial (Mesa)</li>
                <li>Sistema de criação e gestão de salas</li>
                <li>Comunicação em tempo real entre jogadores</li>
                <li>Conteúdo educativo e guias em nosso blog</li>
              </ul>
            </section>

            {/* Idade mínima */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                3. Idade mínima e responsabilidade parental
              </h2>
              <p className="text-justify">
                Nossos serviços são destinados a pessoas de todas as idades. No entanto, estabelecemos as seguintes condições:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Menores de 13 anos devem usar o serviço sob supervisão de um adulto responsável</li>
                <li>Menores entre 13 e 18 anos devem ter consentimento de pais ou responsáveis</li>
                <li>Pais e responsáveis são responsáveis por supervisionar o uso do serviço por menores</li>
              </ul>
            </section>

            {/* Conta de usuário */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                4. Conta de usuário
              </h2>
              <p className="text-justify">
                Para jogar, você pode precisar inserir um nome de usuário. Você é responsável por fornecer informações precisas e
                manter a confidencialidade de qualquer informação que utilize para acessar o serviço.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Você não deve usar nomes ofensivos ou que infrinjam direitos de terceiros</li>
                <li>Não é permitido se passar por outra pessoa</li>
                <li>O uso de nomes que incitem ódio, violência ou discriminação é proibido</li>
              </ul>
            </section>

            {/* Conduta */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                5. Conduta do usuário
              </h2>
              <p className="text-justify">
                Você concorda em utilizar nossos serviços de maneira responsável e respeitosa. É proibido:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Assediar, ameaçar ou intimidar outros usuários</li>
                <li>Compartilhar conteúdo ilegal, ofensivo ou prejudicial</li>
                <li>Interferir no funcionamento do serviço ou tentar explorar vulnerabilidades</li>
                <li>Utilizar bots, scripts ou automações não autorizadas</li>
              </ul>
            </section>

            {/* Propriedade intelectual */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                6. Propriedade intelectual
              </h2>
              <p className="text-justify">
                Todo o conteúdo, marca, logotipos, design e software relacionados ao El Impostor Game são propriedade da RurayLabs
                ou de seus licenciadores. É proibida a reprodução, modificação ou distribuição sem autorização prévia.
              </p>
            </section>

            {/* Disponibilidade */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                7. Disponibilidade e alterações
              </h2>
              <p className="text-justify">
                Podemos modificar, suspender ou descontinuar o serviço total ou parcialmente a qualquer momento. Também podemos
                atualizar estes Termos periodicamente. O uso contínuo do serviço implica aceitação das atualizações.
              </p>
            </section>

            {/* Limitação de responsabilidade */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                8. Limitação de responsabilidade
              </h2>
              <p className="text-justify">
                O serviço é fornecido "como está". Não garantimos que o serviço será ininterrupto ou livre de erros.
                Na medida permitida por lei, não nos responsabilizamos por danos diretos ou indiretos decorrentes do uso do serviço.
              </p>
            </section>

            {/* Privacidade */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                9. Privacidade
              </h2>
              <p className="text-justify">
                O uso do serviço também está sujeito à nossa Política de Privacidade.
              </p>
              <LocalizedLink to="/privacy-policy" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm font-medium">
                Ler Política de Privacidade
              </LocalizedLink>
            </section>

            {/* Rescisão */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                10. Rescisão
              </h2>
              <p className="text-justify">
                Podemos suspender ou encerrar seu acesso ao serviço se identificarmos violação destes Termos ou comportamento
                que prejudique outros usuários. Você também pode deixar de usar o serviço a qualquer momento.
              </p>
            </section>

            {/* Contato */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                11. Contato
              </h2>
              <p className="text-justify">
                Se você tiver dúvidas sobre estes Termos, entre em contato conosco.
              </p>
              <div className="flex flex-wrap gap-3">
                <LocalizedLink to="/contact" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm font-medium">
                  Contato
                </LocalizedLink>
                <LocalizedLink to="/" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm font-medium">
                  Voltar ao início
                </LocalizedLink>
              </div>
            </section>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

const TermsOfServiceEn: React.FC = () => {
  return (
    <BaseLayout>
      <SEOMeta
        title="Terms and Conditions - Impostor Game"
        description="Terms and conditions of use for Impostor Game. Read the rules and conditions for using our game platform."
        canonical="https://el-impostor.com/terms-of-service"
        excludeAdsense={true}
      />
      <div className="min-h-screen bg-blue-50 dark:bg-gray-950 py-8 px-4">
        <div className="max-w-5xl mx-auto bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800">
          {/* Header */}
          <div className="px-8 py-8 border-b border-gray-200 dark:border-gray-800">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3 tracking-tight">Terms and Conditions of Use</h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Last updated:{' '}
              {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div className="px-8 py-8 space-y-8 text-gray-800 dark:text-gray-200 leading-relaxed text-left">
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">1. Welcome and Acceptance</h2>
              <p className="text-justify">
                Welcome to <strong>El Impostor Game</strong>, an online game platform operated by <strong>RurayLabs</strong>.
                By accessing and using our website and services, you agree to be legally bound by these Terms and Conditions.
                If you do not agree with any part of these terms, you must not use our services.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">2. Service Description</h2>
              <p className="text-justify">
                El Impostor Game is an entertainment platform that offers online social deduction games. Our services include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Real-time multiplayer game rooms</li>
                <li>In-person (Table) game mode</li>
                <li>Room creation and management system</li>
                <li>Real-time communication between players</li>
                <li>Educational content and guides on our blog</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">3. Minimum Age and Parental Responsibility</h2>
              <p className="text-justify">
                Our services are designed to be enjoyed by people of all ages. However, we set the following conditions:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Children under 13 must use the service under the supervision of a responsible adult</li>
                <li>Minors between 13 and 18 must have the consent of their parents or guardians</li>
                <li>Parents and guardians are responsible for supervising minors' use of our service</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">4. Use of the Service</h2>
              <p className="text-justify">By using El Impostor Game, you agree to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide truthful information when required</li>
                <li>Not use offensive, vulgar, or inappropriate usernames</li>
                <li>Maintain respectful behavior toward other users</li>
                <li>Not attempt to disrupt, hack, or compromise the security of the service</li>
                <li>Not use unauthorized bots, scripts, or automations</li>
                <li>Not use the service for illegal or fraudulent activities</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">5. Code of Conduct</h2>
              <p className="text-justify">To maintain a positive and safe game environment, we strictly prohibit:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Offensive, threatening, harassing, or discriminatory language</li>
                <li>Sexually explicit or pornographic content</li>
                <li>Hate speech based on race, religion, gender, sexual orientation, or any other characteristic</li>
                <li>Spam, unsolicited advertising, or promotion of external services</li>
                <li>Impersonation or identity misrepresentation of other users</li>
                <li>Sharing other users' personal information without consent</li>
                <li>Cheating or attempting to manipulate game outcomes</li>
              </ul>
              <p className="text-justify mt-4">
                <strong>We reserve the right to suspend or permanently ban</strong> any user who violates these rules.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">6. User Content</h2>
              <p className="text-justify">
                The content you generate during gameplay (messages, names, etc.) is your responsibility. By posting content on our platform:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You declare that you have the right to publish such content</li>
                <li>You grant us a non-exclusive license to display and store that content</li>
                <li>You agree that we may moderate, edit, or remove inappropriate content</li>
                <li>The content must not store sensitive personal information</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">7. Intellectual Property</h2>
              <p className="text-justify">All content of El Impostor Game, including but not limited to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Design, graphics, logos, and visual interface</li>
                <li>Source code and software</li>
                <li>Texts, blog articles, and written content</li>
                <li>Game mechanics and rules system</li>
              </ul>
              <p className="text-justify mt-4">
                Is protected by copyright and intellectual property laws.
                <strong> Copying, reproducing, distributing, or creating derivative works</strong> is not permitted without our express written consent.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">8. Availability and Maintenance</h2>
              <p className="text-justify">We strive to keep the service available 24/7, but:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We may perform scheduled maintenance with or without prior notice</li>
                <li>The service may experience interruptions due to technical issues</li>
                <li>We do not guarantee uninterrupted availability of the service</li>
                <li>We may modify, suspend, or discontinue features without prior notice</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">9. Limitation of Liability</h2>
              <p className="text-justify">
                El Impostor Game is provided "as is" and "as available". To the maximum extent permitted by law:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We are not responsible for data loss or service interruptions</li>
                <li>We do not guarantee that the service is free of errors or viruses</li>
                <li>We are not responsible for the behavior of other users</li>
                <li>We are not responsible for indirect, incidental, or consequential damages</li>
                <li>The service is free and used at your own risk</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">10. Advertising and Third-Party Services</h2>
              <p className="text-justify">
                Our service may display third-party advertising through Google AdSense. By using our service:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You agree that we may display ads on the platform</li>
                <li>Ads may use cookies for personalization</li>
                <li>We do not control the content of third-party ads</li>
                <li>We are not responsible for advertisers' privacy policies</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">11. Changes to the Terms</h2>
              <p className="text-justify">
                We reserve the right to modify these Terms and Conditions at any time. When we make significant changes:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We will update the "Last updated" date</li>
                <li>Changes will take effect immediately upon publication</li>
                <li>Continued use of the service implies acceptance of the new terms</li>
                <li>It is your responsibility to review these terms periodically</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">12. Termination of Service</h2>
              <p className="text-justify">We reserve the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Suspend or terminate your access for violating these terms</li>
                <li>Close game rooms that violate our rules</li>
                <li>Remove inappropriate content without prior notice</li>
                <li>Modify or discontinue the service at any time</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">13. Governing Law and Jurisdiction</h2>
              <p className="text-justify">
                These Terms and Conditions are governed by the laws of Spain. Any dispute related to these terms shall be submitted to the exclusive jurisdiction of the Spanish courts.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">14. Contact</h2>
              <p className="text-justify">If you have questions about these Terms and Conditions, you can contact us:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Through our{' '}
                  <LocalizedLink to="/contact" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium">
                    contact page
                  </LocalizedLink>
                </li>
                <li>Company: RurayLabs</li>
                <li>
                  Website:{' '}
                  <a href="https://el-impostor.com" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline">
                    el-impostor.com
                  </a>
                </li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">15. Severability</h2>
              <p className="text-justify">
                If any provision of these Terms is deemed invalid or unenforceable, the remaining provisions will remain in full force and effect.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">16. Entire Agreement</h2>
              <p className="text-justify">
                These Terms and Conditions, together with our{' '}
                <LocalizedLink to="/privacy-policy" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium">
                  Privacy Policy
                </LocalizedLink>
                , constitute the entire agreement between you and RurayLabs regarding the use of El Impostor Game.
              </p>
            </section>
          </div>

          {/* Document footer */}
          <div className="px-8 py-6 bg-gray-50 dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800">
            <p className="text-sm text-gray-600 dark:text-gray-300 text-center">By continuing to use El Impostor Game, you agree to these Terms and Conditions.</p>
            <div className="flex justify-center gap-4 mt-4">
              <LocalizedLink to="/privacy-policy" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm font-medium">
                View Privacy Policy
              </LocalizedLink>
              <LocalizedLink to="/contact" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm font-medium">
                Contact
              </LocalizedLink>
              <LocalizedLink to="/" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm font-medium">
                Back to Home
              </LocalizedLink>
            </div>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

const TermsOfService: React.FC = () => {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const isPt = i18n.language === 'pt';

  usePageTitle(isEn ? 'Terms and Conditions - Impostor Game' : isPt ? 'Termos e Condições - Impostor Game' : 'Términos y Condiciones - El Impostor Game');

  if (isEn) return <TermsOfServiceEn />;
  if (isPt) return <TermsOfServicePt />;
  return <TermsOfServiceEs />;
};

export default TermsOfService;
